import { useState, useTransition } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "~/components/Button";
import type { CustomerSearchResult } from "~/types";

import { CustomerPicker } from "./CustomerPicker";

export function ApiForm({ onSuccess }: { onSuccess?: () => void }) {
  const navigate = useNavigate();
  const [isPending, startTransition] = useTransition();
  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerSearchResult | null>(null);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedCustomer) return;
    const customerId = selectedCustomer.id;

    // @ts-expect-error not sure why this is a type error; should be fine
    startTransition(async () => {
      try {
        const response = await fetch(
          `/api/customers/${customerId}/cluster_api_key`,
          {
            method: "POST",
            credentials: "include",
          },
        );

        if (response.ok) {
          const data = await response.json();
          if (onSuccess) {
            onSuccess();
          }
          navigate("/", {
            state: {
              flash: data.message,
            },
          });
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error(
            "Error:",
            errorData.message || "Failed to create API key",
          );
        }
      } catch (error) {
        console.error("Error:", error);
      }
    });
  };

  return (
    <form method="post" className="mt-4 space-y-4" onSubmit={handleSubmit}>
      <div>
        <label htmlFor="customer_id" className="mb-1 block text-sm">
          Customer
        </label>
        <CustomerPicker
          id="customer_id"
          disabled={isPending}
          value={selectedCustomer}
          onChange={setSelectedCustomer}
        />
        <p className="mt-1 text-xs text-gray-500">
          Don&apos;t see the customer?{" "}
          <a
            href="https://turbopuffer.com/dashboard/admin/customers"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline hover:text-blue-800"
          >
            Create one in the dashboard
          </a>
          .
        </p>
      </div>
      <Button
        className="rounded bg-gray-100 px-4 py-2 text-sm hover:bg-gray-200"
        type="submit"
        disabled={isPending || !selectedCustomer}
      >
        {isPending ? "Generating..." : "Generate API Key"}
      </Button>
    </form>
  );
}
